import { Clapperboard } from 'lucide-react';
import { ActionBar, Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Skeleton } from '@vnetwork/ui/primitives';
import { useVerimatrixes } from '@/api/media/shared-verimatrix';
import { vodVerimatrixConfig } from '@/api/media/configs';
import { useJobCreateForm } from './use-job-create-form';
import { JobCreateSourceSection } from './JobCreateSourceSection';
import { JobCreateOutputSection } from './JobCreateOutputSection';
import { JobCreateDistributionSection } from './JobCreateDistributionSection';
import { JobCreateNotificationSection } from './JobCreateNotificationSection';
import styles from './JobCreate.module.css';

interface Props {
  serviceId: string;
}

export function VodJobCreate({ serviceId }: Props) {
  const {
    data: verimatrixes = [],
  } = useVerimatrixes(serviceId, vodVerimatrixConfig);

  const {
    form,
    currentStorages,
    selectedStorage,
    presets,
    notifications,
    isPageLoading,
    isSubmitting,
    canSubmit,
    formError,
    fieldErrors,
    updateSource,
    updateMd5sum,
    addAdditionalTrack,
    removeAdditionalTrack,
    togglePreset,
    isOutputSelected,
    toggleOutputCard,
    toggleCardOption,
    updateOutputOption,
    updateGlobalOverride,
    updateDistributionDraft,
    updateDistributionGroupDraft,
    updateNotification,
    getStoragesByTarget,
    handleSubmit,
    navigate,
  } = useJobCreateForm(serviceId);

  const base = `/services/vod/${serviceId}`;

  if (isPageLoading) {
    // Skeleton form blocks instead of a full-screen backdrop while source config
    // (storages, presets, verimatrix) loads.
    return (
      <Container size="lg">
        <PageHeader
          icon={<Clapperboard size={22} />}
          title="Create Job"
          description="Configure source, output presets, distribution and notifications for a new encoding job."
        />
        <div className={styles.skeletonGrid}>
          <Skeleton variant="rect" height={160} />
          <Skeleton variant="rect" height={160} />
          <Skeleton variant="rect" height={120} />
        </div>
      </Container>
    );
  }

  return (
    <Container size="lg">
      <PageHeader
        icon={<Clapperboard size={22} />}
        title="Create Job"
        description="Configure source, output presets, distribution and notifications for a new encoding job."
      />
      <form onSubmit={(e) => void handleSubmit(e)} className={styles.form}>
      {formError && <div className={styles.formError}>{formError}</div>}

      <JobCreateSourceSection
        form={form}
        currentStorages={currentStorages}
        selectedStorage={selectedStorage}
        updateSource={updateSource}
        updateMd5sum={updateMd5sum}
        addAdditionalTrack={addAdditionalTrack}
        removeAdditionalTrack={removeAdditionalTrack}
        fieldErrors={{ sourcePath: fieldErrors.sourcePath }}
      />

      <JobCreateOutputSection
        form={form}
        presets={presets}
        verimatrixes={verimatrixes}
        togglePreset={togglePreset}
        isOutputSelected={isOutputSelected}
        toggleOutputCard={toggleOutputCard}
        toggleCardOption={toggleCardOption}
        updateOutputOption={updateOutputOption}
        updateGlobalOverride={updateGlobalOverride}
        fieldErrors={{ presets: fieldErrors.presets, outputs: fieldErrors.outputs }}
      />

      <JobCreateDistributionSection
        form={form}
        isOutputSelected={isOutputSelected}
        getStoragesByTarget={getStoragesByTarget}
        updateDistributionDraft={updateDistributionDraft}
        updateDistributionGroupDraft={updateDistributionGroupDraft}
      />

      <JobCreateNotificationSection
        form={form}
        notifications={notifications}
        updateNotification={updateNotification}
      />

      <ActionBar
        start={
          <Button
            variant="ghost"
            type="button"
            disabled={isSubmitting}
            onClick={() => navigate(`${base}/jobs`)}
          >
            Cancel
          </Button>
        }
        end={
          <Button type="submit" disabled={!canSubmit}>
            {isSubmitting ? 'Creating…' : 'Create job'}
          </Button>
        }
      />
      </form>
    </Container>
  );
}

export default VodJobCreate;
